import {patchState, signalStore, withMethods, withState} from '@ngrx/signals';
import {GlobalUser, User, Company, Customer} from '@ew/models';
import {GlobalUserState} from './global-user.state';

/**
 * Initial state of the global user store.
 * Represents an unauthenticated user with empty user, company, and customer objects.
 */
const initialState: GlobalUser = {
  user: {} as User,
  company: {} as Company,
  customer: {} as Customer,
  authenticated: false,
};

/**
 * Global User Store - Application-wide user authentication and profile state.
 *
 * This NgRx Signals store manages the globally authenticated user's information.
 * It's a root-level store that provides user data throughout the entire application.
 *
 * The store maintains:
 * - User account information (name, email, photo, etc.)
 * - Company information (if user is a business owner)
 * - Customer information (if user books services)
 * - Authentication status
 *
 * This store is populated after successful authentication and is used by:
 * - Auth guards to check authentication status
 * - Components to display user information
 * - Services to send user-specific API requests
 *
 * @example
 * // In a component
 * export class ProfileComponent {
 *   private globalUserStore = inject(GlobalUserStore);
 *
 *   user = this.globalUserStore.user;
 *   company = this.globalUserStore.company;
 *   isAuthenticated = this.globalUserStore.authenticated;
 *
 *   updateName(newName: string) {
 *     this.globalUserStore.updateName(newName);
 *   }
 * }
 *
 * @example
 * // In a service
 * export class SomeService {
 *   private globalUserStore = inject(GlobalUserStore);
 *
 *   getUserId() {
 *     return this.globalUserStore.user().id;
 *   }
 * }
 *
 * @see GlobalUser - The complete user state model
 * @see User - User account model
 * @see Company - Company/business model
 * @see Customer - Customer profile model
 * @see AuthService - Service that populates this store
 */
export const GlobalUserStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),
  //#region Methods
  withMethods((store) => ({
    /**
     * Resets the store to its initial unauthenticated state.
     * Clears all user, company, and customer information.
     * Typically called during logout.
     *
     * @returns {void}
     */
    unset: () => {
      patchState(store, (state) => ({
        ...state,
        ...initialState,
      }));
    },

    /**
     * Sets the complete global user state including user, company, and customer.
     * This is the primary method called after successful authentication.
     * Also updates the global UserRole state if available.
     *
     * @param {GlobalUser} userGlobal - The complete global user data from authentication
     * @returns {void}
     */
    setGlobalUser: (userGlobal: GlobalUser) => {
      patchState(store, (state) => ({
        ...state,
        ...userGlobal,
      }));

      if (userGlobal.user.role) {
        GlobalUserState.userRole = userGlobal.user.role;
      }
    },

    /**
     * Sets the user object in the global state.
     * 
     * @param user - The new user data to update the state with.
     */
    setUser: (user: User) => {
      patchState(store, (state) => ({
        ...state,
        user,
      }));
    },

    /**
     * Updates the user's name in the store.
     *
     * @param {string} name - The new name for the user
     * @returns {void}
     */
    updateName: (name: string) => {
      patchState(store, (state) => ({
        ...state,
        user: {
          ...state.user,
          name,
        },
      }));
    },

    /**
     * Patches any subset of basic-info fields on the user (name, jobTitle,
     * bio). Mirrors the backend's partial-update endpoint.
     */
    updateBasicInfo: (patch: {name?: string; jobTitle?: string; bio?: string}) => {
      patchState(store, (state) => ({
        ...state,
        user: {
          ...state.user,
          ...(patch.name !== undefined ? {name: patch.name} : {}),
          ...(patch.jobTitle !== undefined ? {jobTitle: patch.jobTitle} : {}),
          ...(patch.bio !== undefined ? {bio: patch.bio} : {}),
        },
      }));
    },

    /**
     * Updates the user's profile photo path.
     *
     * @param {string} fullPhotoPath - The complete URL path to the user's photo
     * @returns {void}
     */
    updateUserPhoto: (fullPhotoPath: string) => {
      patchState(store, (state) => ({
        ...state,
        user: {
          ...state.user,
          photoPath: fullPhotoPath,
        },
      }));
    },

    /**
     * Updates the user's phone number.
     *
     * @param {string} phoneNumber - The new phone number for the user
     * @returns {void}
     */
    updatePhoneNumber: (phoneNumber: string) => {
      patchState(store, (state) => ({
        ...state,
        user: {
          ...state.user,
          phone: phoneNumber,
        },
      }));
    },

    /**
     * Updates the user's email address.
     *
     * @param {string} email - The new email address for the user
     * @returns {void}
     */
    updateEmail: (email: string) => {
      patchState(store, (state) => ({
        ...state,
        user: {
          ...state.user,
          email,
        },
      }));
    },

    /**
     * Updates the user's physical address.
     *
     * @param {string} address - The new address for the user
     * @returns {void}
     */
    updateAddress: (address: string) => {
      patchState(store, (state) => ({
        ...state,
        user: {
          ...state.user,
          address,
        },
      }));
    },

    /**
     * Updates the company in the global user state with partial data.
     *
     * @param company - Partial company data to update.
     */
    updateCompany: (company: Partial<Company>) => {
      patchState(store, (state) => ({
        ...state,
        company: {
          ...state.company,
          ...company,
        },
      }));
    },

    /**
     * Sets the company object in the global state.
     *
     * @param company - The new company data to update the state with.
     */
    setCompany: (company: Company) => {
      patchState(store, (state) => ({
        ...state,
        company,
      }));
    },

    /**
     * Updates the user in the global user state with partial data.
     *
     * @param user - Partial user data to update.
     */
    updateUser: (user: Partial<User>) => {
      patchState(store, (state) => ({
        ...state,
        user: {
          ...state.user,
          ...user,
        },
      }));
    },

    /**
     * Updates the customer in the global user state.
     *
     * @param customer - The new customer data to update the state with.
     */
    setCustomer: (customer: Customer) => {
      patchState(store, (state) => ({
        ...state,
        customer,
      }));
    },

    /**
     * Updates the company profile image.
     *
     * @param photoPath - image url
     */
    updateProfileImg: (photoPath: string) => {
      patchState(store, (state) => ({
        ...state,
        company: {
          ...state.company,
          photoPath,
        },
      }));
    },

    /**
     * Updates the company name.
     *
     * @param name - The new name for the company.
     */
    updateCompanyName: (name: string) => {
      patchState(store, (state) => ({
        ...state,
        company: {
          ...state.company,
          name,
        },
      }));
    },

    /**
     * Updates the company description.
     *
     * @param description - The new description for the company.
     */
    updateCompanyDescription: (description: string) => {
      patchState(store, (state) => ({
        ...state,
        company: {
          ...state.company,
          description,
        },
      }));
    },

    /**
     * Updates the entire customer object in the global user state.
     *
     * @param customer - The updated customer data.
     */
    updateCustomer: (customer: Customer) => {
      const currentCustomer = store.customer?.() ?? ({} as Customer);
      patchState(store, {
        customer: {
          ...currentCustomer,
          ...customer,
        },
      });
    },

    /**
     * Updates the customer profile photo path.
     *
     * @param photoPath - The new photo path for the customer.
     */
    updateCustomerPhoto: (photoPath: string) => {
      const currentCustomer = store.customer?.() ?? ({} as Customer);
      patchState(store, {
        customer: {
          ...currentCustomer,
          photoPath,
        },
      });
    },
  })),

  //#endregion
);
